// frontend/src/components/grid/GridRowActions.jsx
//
// The actions cell of a list grid's row: the row's next step as one button,
// then one ⋯ menu with everything else. The purchase order, purchase
// recommendation and promotion lists all render their rows through it, so the
// cell looks and clicks the same on each. Which actions a row offers is the
// caller's rule (poActions.js, recommendationRowActions.js,
// promotionActions.js); this draws the answer.
//
//   <GridRowActions
//     primary={<GridPrimaryButton label=… alternatives=[…] onClick=… />}
//     items={[…]}             // ActionMenu entries
//     menuLoading={bool}      // a file the menu started is being generated
//   />
//
// `primary` is any element (a blocked row shows a chip there) or null. The
// `danger` entries go into their own group at the bottom of the menu.
//
// <GridPrimaryButton> is as wide as the widest label the column can show
// (`alternatives`, through ChipSlot), so the buttons line up down the column
// in every language.

import { Box, Button, IconButton } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ICON, DotsThree } from '../../icons';
import ActionMenu from '../common/ActionMenu';
import ChipSlot from './ChipSlot';

export function GridPrimaryButton({ label, alternatives, ...buttonProps }) {
  return (
    <Button className="grid-action-btn" variant="contained" {...buttonProps}>
      <ChipSlot shown={<span>{label}</span>} alternatives={alternatives.filter((a) => a !== label)} />
    </Button>
  );
}

export default function GridRowActions({ primary, items, menuLoading = false }) {
  const { t } = useTranslation();
  const grouped = [
    ...items.filter((item) => !item.danger),
    ...items.filter((item) => item.danger).map((item, i) => ({ ...item, separatorBefore: i === 0 })),
  ];
  return (
    // stopPropagation on mousedown so a click never toggles row selection
    // (the grid-wide convention for in-cell controls).
    <Box
      sx={{ display: 'flex', gap: 0.5, alignItems: 'center', justifyContent: 'flex-end', height: '100%' }}
      onMouseDown={(e) => e.stopPropagation()}
    >
      {primary}
      <ActionMenu
        items={grouped}
        renderTrigger={(triggerProps) => (
          <IconButton size="small" aria-label={t('grid.moreActions')} loading={menuLoading} {...triggerProps}>
            <DotsThree size={ICON.md} />
          </IconButton>
        )}
      />
    </Box>
  );
}
